/**
 * Markdown processing utilities for handling HTML elements, sanitization, and plugins
 * @module markdown
 */

import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { remarkDefinitionList } from "remark-definition-list";
import remarkFlexibleMarkers from "remark-flexible-markers";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { SKIP, visit } from "unist-util-visit";

// Dynamic import for KaTeX - only load when needed
let katexLib = null;
const loadKatex = async () => {
  if (!katexLib) {
    const katex = await import("rehype-katex");
    katexLib = katex.default;
  }
  return katexLib;
};

/**
 * List of allowed HTML elements that can be rendered in markdown content
 * @type {string[]}
 */
export const allowedHTMLElements = [
  "a",
  "b",
  "blockquote",
  "br",
  "code",
  "dd",
  "del",
  "details",
  "div",
  "dl",
  "dt",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "i",
  "ins",
  "kbd",
  "li",
  "mark",
  "ol",
  "p",
  "pre",
  "q",
  "rp",
  "rt",
  "ruby",
  "s",
  "samp",
  "source",
  "span",
  "strike",
  "strong",
  "sub",
  "summary",
  "sup",
  "table",
  "tbody",
  "td",
  "tfoot",
  "th",
  "thead",
  "tr",
  "ul",
  "var",
  // KaTeX math elements
  "math",
  "semantics",
  "mrow",
  "mi",
  "mo",
  "mn",
  "mfrac",
  "msup",
  "msub",
  "msubsup",
  "munder",
  "mover",
  "munderover",
  "msqrt",
  "mroot",
  "mtext",
  "mspace",
  "annotation",
];

/**
 * Configuration options for rehype-sanitize plugin
 * Extends the default schema with custom allowed elements and attributes
 */
const rehypeSanitizeOptions = {
  ...defaultSchema,
  tagNames: allowedHTMLElements,
  attributes: {
    ...defaultSchema.attributes,
    div: [
      ...(defaultSchema.attributes?.div ?? []),
      "data*",
      ["className", "__boltArtifact__"],
      ["className", /^katex/],
    ],
    // Allow KaTeX classes and attributes
    span: [
      ...(defaultSchema.attributes?.span ?? []),
      ["className", /^katex/, /^math-/],
      "style",
      "aria-hidden",
    ],
    code: [
      ...(defaultSchema.attributes?.code ?? []),
      ["className", /^language-/, /^math-/],
    ],
    // Math elements attributes
    math: ["display", "xmlns"],
    semantics: [],
    mrow: [],
    mi: ["mathvariant"],
    mo: ["stretchy", "fence", "separator", "lspace", "rspace"],
    mn: [],
    mfrac: ["linethickness"],
    msup: [],
    msub: [],
    msubsup: [],
    munder: [],
    mover: [],
    munderover: [],
    msqrt: [],
    mroot: [],
    mtext: [],
    mspace: ["width"],
    annotation: ["encoding"],
  },
  strip: [],
};

/**
 * Configures remark plugins based on markdown mode
 * @param {boolean} limitedMarkdown - Whether to use limited markdown mode
 * @returns {Array} Array of configured remark plugins
 */
export function remarkPlugins(limitedMarkdown) {
  const plugins = [
    remarkGfm,
    // Add remark-math for LaTeX math support
    remarkMath,
    // Add remark-definition-list for definition list syntax
    remarkDefinitionList,
    // Add remark-flexible-markers for ==text== highlighting syntax
    [
      remarkFlexibleMarkers,
      {
        markerClassName: "highlight-marker",
        markerTagName: "mark",
      },
    ],
    // remarkEmoji,
    // remarkSubSuper,
    // remarkDirective,
    highlightDirectivePlugin,
  ];

  if (limitedMarkdown) {
    plugins.unshift(limitedMarkdownPlugin);
  }

  return plugins;
}

/**
 * Configures rehype plugins for HTML processing
 * @param {boolean} html - Whether HTML processing is enabled
 * @param {boolean} enableMath - Whether to enable math rendering (optional)
 * @returns {Array} Array of configured rehype plugins
 */
export function rehypePlugins(html, enableMath = true) {
  const plugins = [];

  if (html) {
    plugins.push(rehypeRaw);
  }

  // Add rehype-katex for LaTeX math rendering with proper configuration
  // Only add if math is enabled and will be used
  if (enableMath) {
    plugins.push([
      async () => {
        const katex = await loadKatex();
        return katex;
      },
      {
        // Configure KaTeX options
        throwOnError: false, // Don't throw on math errors, show error in output instead
        errorColor: "#cc0000", // Color for error text
        strict: false, // Allow some non-standard LaTeX
      },
    ]);
  }
  // plugins.push(rehypeHighlight); // Disabled - using custom CodeBlock component instead

  if (html) {
    plugins.push([rehypeSanitize, rehypeSanitizeOptions]);
  }

  return plugins;
}

/**
 * Plugin that limits markdown syntax to basic elements
 * Converts unsupported nodes to plain text while preserving content
 * @returns {Function} Transformer function for the remark plugin
 */
const limitedMarkdownPlugin = () => {
  return (tree, file) => {
    const contents = file.toString();

    visit(tree, (node, index, parent) => {
      if (
        index == null ||
        [
          "paragraph",
          "text",
          "inlineCode",
          "code",
          "strong",
          "emphasis",
        ].includes(node.type) ||
        !node.position
      ) {
        return true;
      }

      let value = contents.slice(
        node.position.start.offset,
        node.position.end.offset
      );

      if (node.type === "heading") {
        value = `\n${value}`;
      }

      parent.children[index] = {
        type: "text",
        value,
      };

      return [SKIP, index];
    });
  };
};

/**
 * Plugin to handle highlight directives (::highlight[text])
 * Converts ::highlight[text] directives to <mark> elements
 */
const highlightDirectivePlugin = () => {
  return (tree) => {
    visit(tree, (node) => {
      if (
        node.type === "textDirective" ||
        node.type === "leafDirective" ||
        node.type === "containerDirective"
      ) {
        if (node.name === "highlight") {
          const data = node.data || (node.data = {});
          const tagName = "mark";

          data.hName = tagName;
          data.hProperties = {
            className: ["highlight-custom"],
          };
        }
      }
    });
  };
};

export default {
  allowedHTMLElements,
  remarkPlugins,
  rehypePlugins,
};
